import { DatePipe, formatDate } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
} from '@angular/forms';
import { IonDatetime } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

interface OutputDate {
  start?: string;
  end?: string;
}

enum DatePickerMode {
  POPOVER = 'popover',
  MODAL = 'modal',
}

enum DatePickerPresentation {
  DATE = 'date',
  TIME = 'time',
}

@Component({
  selector: 'app-datetime-picker',
  templateUrl: 'datetime-picker.template.html',
  styleUrls: ['./datetime-picker.style.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DateTimePickerComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: DateTimePickerComponent,
    },
  ],
})
export class DateTimePickerComponent
  implements OnInit, ControlValueAccessor, OnChanges
{
  @ViewChild('dateStart', { static: false }) dateStart: IonDatetime;
  @ViewChild('dateStart', { static: false, read: ElementRef })
  dateStartRef: ElementRef;
  @ViewChild('dateEnd', { static: false }) dateEnd: IonDatetime;
  @ViewChild('dateEnd', { static: false, read: ElementRef })
  dateEndRef: ElementRef;
  @Input() startEnd: boolean;
  @Input() presentation: DatePickerPresentation = DatePickerPresentation.DATE;
  @Input() mode: DatePickerMode = DatePickerMode.MODAL;
  @Input() requiredBorder: boolean;
  @Input() touched: boolean;
  @Input() validateError: boolean;
  @Input() startTitle: string;
  @Input() startToday: boolean = true;
  @Input() customUse: boolean = false;

  focusDateEnd: string = new Date().toISOString();
  focusDateStart: string = new Date().toISOString();

  public onChange = (dataChange) => {};
  public onTouched = () => {};
  public disabled: boolean = false;
  public defaultMinDate: string;
  @Output() datesChanged: EventEmitter<OutputDate> =
    new EventEmitter<OutputDate>();

  public endTitle: string = 'datepicker.end';
  public locale: string = 'datepicker.locale';
  public outputDate: OutputDate;

  private calendarMonthObserver = new MutationObserver((mutationRecords) => {
    if (mutationRecords) {
      window.dispatchEvent(
        new CustomEvent('datetimeMonthDidChange', { detail: mutationRecords }),
      );
    }
  });

  constructor(
    private elem: ElementRef,
    private translate: TranslateService,
    private datePipe: DatePipe,
  ) {
    if (!this.startTitle) {
      this.startTitle = 'datepicker.start';
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (this.outputDate?.start && this.outputDate?.end) {
      const startDate = this.datePipe.transform(
        this.outputDate?.start as string,
        'MM/dd/yyyy',
      );
      this.focusDateStart = new Date(startDate).toISOString();
      const endDate = this.datePipe.transform(
        this.outputDate?.end as string,
        'MM/dd/yyyy',
      );
      this.focusDateEnd = new Date(endDate).toISOString();

      const resetEnd = this.elem.nativeElement.querySelector('#resetButtonEnd');
      if (resetEnd) resetEnd.click();
      const resetStart =
        this.elem.nativeElement.querySelector('#resetButtonStart');
      if (resetStart) resetStart.click();
    }
  }

  ngOnInit() {
    this.asyncRemoveBackDrop();
    this.defaultMinDate = this.startToday
      ? new Date().toISOString()
      : new Date('01/01/1900').toISOString();
  }

  public asyncRemoveBackDrop(): void {
    const removeInterval = setInterval(() => {
      const hasClass = document
        .querySelector('body')
        .classList.contains('backdrop-no-scroll');

      if (hasClass) {
        this.removeBackdrop();
        clearInterval(removeInterval);
      }
    }, 10);
  }

  public setBackdrop(): void {
    document.querySelector('body').classList.add('backdrop-no-scroll');
  }

  public removeBackdrop(): void {
    document.querySelector('body').classList.remove('backdrop-no-scroll');
  }

  writeValue(date: OutputDate) {
    const defaultValues: OutputDate = this.startEnd
      ? {
          start: null,
          end: null,
        }
      : { start: null };

    this.outputDate = !date ? defaultValues : date;
    this.onChange(this.outputDate);
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  public dateChanged(event: CustomEvent, changedStart = false): void {
    this.markAsTouched();
    const dateTarget: HTMLElement = event.target as HTMLElement;
    const targetId: string = dateTarget.getAttribute('data-id');
    const newDate: Date = event.detail.value;

    if (!newDate) return;

    Object.assign(this.outputDate, {
      [targetId]: newDate,
    });

    if (this.startEnd && changedStart) {
      const startDate = this.datePipe.transform(
        this.dateStart.value as string,
        'MM/dd/yyyy',
      );
      this.focusDateEnd = new Date(startDate).toISOString();

      this.elem.nativeElement.querySelector('#resetButtonEnd').click();
    }

    this.datesChanged.emit(this.outputDate);
    this.onChange(this.outputDate);

    if (!!this.dateStart) {
      const isSelectingMonth = (
        this.dateStartRef.nativeElement as HTMLElement
      ).classList.contains('show-month-and-year');
      if (!isSelectingMonth) this.dateStart.confirm(true);
    }

    if (!!this.dateEnd) {
      const isSelectingMonth = (
        this.dateEndRef.nativeElement as HTMLElement
      ).classList.contains('show-month-and-year');
      if (!isSelectingMonth) this.dateEnd.confirm(true);
    }
  }

  private checkAllKeys(obj: Object): boolean {
    return !Object.keys(obj).find((key) => !obj[key]);
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.customUse) return;

    if (!this.touched) return;

    const date: OutputDate = this.outputDate;
    const validate = !this.checkAllKeys(date);

    this.validateError = validate;

    return validate ? { required: date } : null;
  }
}
